import React from "react";
import { createRoot } from "react-dom/client";
import "./styles/tokens.css";
import "./index.css";
import App from "./App";
import theme from "./theme";
import { ThemeProvider } from "@mui/material/styles";
import store from "./app/store";
import { Provider } from "react-redux";
import * as serviceWorker from "./serviceWorker";

import DataProvider from "provider";
import Analytics from "analytics";
import posthogAdapter from "analytics/adapters/posthog";
import consoleAdapter from "analytics/adapters/console";

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST;

// Resolve (never reject) once `promise` settles or `ms` elapses, so a slow or
// hanging async dependency can't block the app from ever mounting. Some envs —
// e.g. a PR preview domain that isn't registered with Clerk, or a mobile
// browser whose ITP rules block Clerk's cross-domain storage — make
// clerk.load() reject or hang indefinitely; we must still render so public
// routes (home, 404) work.
const settleOrTimeout = (promise, ms, label) =>
  Promise.race([
    Promise.resolve(promise).catch((error) => {
      console.error(`${label} failed; continuing`, error);
    }),
    new Promise((resolve) =>
      setTimeout(() => {
        console.warn(`${label} did not finish within ${ms}ms; rendering anyway`);
        resolve();
      }, ms)
    ),
  ]);

async function initializeApp() {
  // PostHog when a key is configured (staging/prod), console adapter for local dev.
  if (POSTHOG_KEY) {
    Analytics.register(posthogAdapter);
    await settleOrTimeout(
      Analytics.init(POSTHOG_KEY, { host: POSTHOG_HOST, env: process.env.REACT_APP_ENV }),
      4000,
      "Analytics.init"
    );
  } else {
    Analytics.register(consoleAdapter);
    await Analytics.init(null, { env: process.env.REACT_APP_ENV });
  }

  await settleOrTimeout(DataProvider.initialize(), 5000, "DataProvider.initialize");

  const container = document.getElementById("root");
  const root = createRoot(container);
  
  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <App />
        </ThemeProvider>
      </Provider>
    </React.StrictMode>
  );
}

initializeApp();

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
